import {Injectable} from "@angular/core";
import {Receipt} from "../types/receipt";
import {ReceiptStore} from "./receiptstore";
import {UtilsService} from "./utils";

import {StorageService} from "./storage.service";
import {AlertController, LoadingController, ModalController} from "@ionic/angular";
import {TranslateService} from "@ngx-translate/core";
import {PrintService} from "./print.service";
import {CardPaymentService} from "./cardpayment.service";
import { CardTerminalProcessingModalPage } from "../modal/card-terminal-processing-modal/card-terminal-processing-modal.page";
import { Logger, LoggingService } from "ionic-logging-service";
import { RestolutionService } from "./restolution";
import { ReceiptRow } from "../types/receiptrow";
import { BehaviorSubject } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class ReceiptService {

  private static storageKeyPrefix = "receipt";

  public receipt: Receipt;

  private receiptNumber = new BehaviorSubject<any>(1);
  // eslint-disable-next-line @typescript-eslint/member-ordering
  receiptNrStream = this.receiptNumber.asObservable();

  private logger: Logger;

  private receiptWithCondiments = new BehaviorSubject<any>({
    receipt: { rows: [] }
  });

  // eslint-disable-next-line @typescript-eslint/member-ordering
  receiptWithCondimentsStream = this.receiptWithCondiments.asObservable();

  constructor(private receiptStore: ReceiptStore, private resto: RestolutionService, private utils: UtilsService,
    private printService: PrintService, private cardPayment: CardPaymentService, private storageService: StorageService,
    private translate: TranslateService, private alertController: AlertController, private modalController: ModalController,
    loggingService: LoggingService)
  {
    this.logger = loggingService.getLogger("BestoGo.ReceiptService");
  }

  initReceiptData() {
    this.logger.trace("initReceiptData", "Inititalized receipt");
    this.receipt = {
      methodOfPayment: "CARD",
      // eslint-disable-next-line quote-props
      // eslint-disable-next-line id-blacklist
      number: -1,
      date: new Date(),
      clientID: this.resto.currentLogin.clientId,
      vats: {},
      guid: this.utils.generateGUIDString(),
      discounts: [],
      payments: [],
      rows: [],
      total: 0,
      printOut: "",
    };
    this.receiptWithCondiments.next({ receipt: this.utils.convertRowsToCondiments(this.receipt) });
    /* this.calcTotals(); */
    this.getRunningReceiptNumber()
      .then((runningNr) => {
        this.logger.info("initReceiptData", "New running number is " + runningNr);
        // eslint-disable-next-line id-blacklist
        this.receipt.number = runningNr;
      /*  this.calcTotals(); */
      })
      .catch((error) => {
        this.logger.error("initReceiptData", "Error: " + error);
      });
  }

  setReceipt(newReceipt: Receipt) {
    this.receipt = newReceipt;
    this.receiptWithCondiments.next({ receipt: this.utils.convertRowsToCondiments(newReceipt) });
  }

  async initReceiptNumber(runningNumber: number) {
    await this.storageService.set(StorageService.receiptNumberKey, runningNumber);
  }

  async getRunningReceiptNumber(): Promise<number> {
    const nr = await this.storageService.get(StorageService.receiptNumberKey);
    if (!nr) {
      this.initReceiptNumber(this.receiptNumber.value);
      return nr;
    } else {
      this.receiptNumber.next(nr);
      return nr;
    }
  }

  async increaseAndGetReceiptNumber() {
    let nr = this.receiptNumber.value;
    nr++;
    await this.initReceiptNumber(nr);
    this.receiptNumber.next(nr);
    return nr;
  }

  getNextRowPosition() {
    if (!this.receiptWithCondiments.value.receipt.rows || this.receiptWithCondiments.value.receipt.rows.length === 0) {
      return 0;
    } else {
      return this.receiptWithCondiments.value.receipt.rows.reduce((a, b)=> a.rowPosition > b.rowPosition ? a : b).rowPosition + 1;
    }
  }

  async void(activeReceipt: Receipt) {
    const r2: Receipt = this.utils.clone(activeReceipt);
    this.logger.debug("void", "r2", r2);
    r2.cancelReceipt = true;
    for (const r of r2.rows) {
      r.price = -r.price;
    }
    r2.total = -activeReceipt.total;
    for (const p of r2.payments) {
      p.realSum = -p.realSum;
    }
    // eslint-disable-next-line id-blacklist
    r2.number = -r2.number;
    const keys = Object.keys(r2.vats);
    for (const k of keys) {
      r2.vats[k].totalWithoutVat = -r2.vats[k].totalWithoutVat;
      r2.vats[k].totalSum = -r2.vats[k].totalSum;
      r2.vats[k].totalVat = -r2.vats[k].totalVat;
    }
    let setToRefund = false;
    const receiptNumber = await this.getRunningReceiptNumber();
    this.logger.debug("void", "Got receiptnumber", receiptNumber);
    // eslint-disable-next-line id-blacklist
    r2.number = receiptNumber;
    activeReceipt.voidedByUuid = r2.guid;
    activeReceipt.voidedByNumber = r2.number;
    r2.guid = this.utils.generateGUIDString();
    r2.originalUuid = activeReceipt.guid;
    r2.originalNumber = activeReceipt.number;
    if (r2.payments) {
      for (const rr of r2.payments) {
        if (rr.payment === "CARD") {
          setToRefund = true;
          const modal = await this.modalController.create({
            component: CardTerminalProcessingModalPage,
            cssClass: "bestogo-modal modal-xs",
            componentProps: {
              receipt: r2,
            }
          });
          await modal.present();
          this.cardPayment.refund(r2, rr).subscribe((rr2) => {
            rr.cardTerminalTransactionId = rr2.cardTerminalTransactionId;
            rr.rawPtData = rr2.rawPtData;
            rr.authorization  = rr2.authorization;
            r2.printOut = null;
            this.receiptStore.addReceiptForSending(r2);
            this.increaseAndGetReceiptNumber();
            activeReceipt.hasCancelReceipt = true;
            this.receiptStore.writeToStorage();
            this.printService.print(r2).subscribe((t) => {
              this.logger.debug("void", "Printed", t);
            });
            modal.dismiss();
          }, (error) => {
            modal.dismiss();
            this.alertController
              .create({
                cssClass: "generic-error",
                header: "Error",
                subHeader: "Technical problem with refunding",
                message: error,
                buttons: ["OK"],
              })
              .then((alertCreated) => {
                alertCreated.present();
              });
          });
        }
      }
      if (!setToRefund) {
        r2.printOut = null;
        this.receiptStore.addReceiptForSending(r2);
        await this.increaseAndGetReceiptNumber();
        activeReceipt.hasCancelReceipt = true;
        this.receiptStore.writeToStorage();
        this.printService.print(r2).subscribe((t) => {
          this.logger.debug("Printed", t);
        });
      }
    }
  }

  async createVoidAlert(activeReceipt: Receipt) {
    const alert = await this.alertController.create({
      header: this.translate.instant("Confirm"),
      message: this.translate.instant("Are you sure you want to void receipt?"),
      cssClass: "generic-confirmation",
      buttons: [
        {
          text:  this.translate.instant("Cancel"),
          role: "cancel",
          cssClass: "secondary",
          handler: () => {},
        },
        {
          text:  this.translate.instant("Yes"),
          cssClass: "danger",
          handler: async () => {
            this.void(activeReceipt);

          },
        },
      ],
    });
    return alert;
  }
}
